import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBrowsePageStore } from './useBrowsePageStore';
import { useAnswerAuditInvitationMutation } from './useAnswerAuditInvitationMutation';

interface ConfirmAuditInvitationModalProps {
  osvStatement: string | null;
}

const ConfirmAuditInvitationModal = ({ osvStatement }: ConfirmAuditInvitationModalProps) => {
  const { t, i18n } = useTranslation();
  const browseViewStore = useBrowsePageStore();
  const isOpen = browseViewStore.modalState === 'confirm-audit-invitation';
  const { mutate, isLoading } = useAnswerAuditInvitationMutation();

  const handleClose = () => {
    if (isLoading) return;
    browseViewStore.setModalState('hidden');
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <DialogTitle>{t('Are you sure?', { defaultValue: 'Are you sure?' })}</DialogTitle>
      {osvStatement ? (
        <DialogContent sx={{ pt: 0 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: osvStatement }} />
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 0 }}>
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.grey[700],
            })}
          >
            {t('Are you sure you want to confirm audit invitation ({{auditInvitationShowAs}})?', {
              defaultValue:
                'Are you sure you want to confirm audit invitation ({{auditInvitationShowAs}})?',
              auditInvitationShowAs: browseViewStore.auditInvitation?.uId,
            })}
          </Typography>

          <Typography
            variant="body1"
            sx={(theme) => ({
              mt: '1rem',
              color: theme.palette.grey[700],
            })}
          >
            <span>
              {t(
                'I declare that I have not had the following contacts with the audited organization in the past three years',
                {
                  defaultValue:
                    '1. I declare that I have not had the following contacts with the audited organization in the past three years:',
                }
              )}
            </span>
            <ul>
              <li>
                {t('I was not a regular or contractual employee of the organization', {
                  defaultValue: 'I was not a regular or contractual employee of the organization',
                })}
              </li>
              <li>
                {t('I did not perform an assessment (outside SIQ)', {
                  defaultValue: 'I did not perform an assessment (outside SIQ)',
                })}
              </li>
              <li>
                {t('counseling', {
                  defaultValue: 'counseling',
                })}
              </li>
              <li>
                {t('education (via SIQ or outside SIQ)', {
                  defaultValue: 'education (via SIQ or outside SIQ)',
                })}
              </li>
              <li>
                {t(
                  'in the audited organization or its business competition, I did not provide consulting in relation to the development, construction, marketing or maintenance of the products that are the subject of the audit and',
                  {
                    defaultValue:
                      'in the audited organization or its business competition, I did not provide consulting in relation to the development, construction, marketing or maintenance of the products that are the subject of the audit and',
                  }
                )}
              </li>
              <li>
                {t(
                  'the company in which I am employed has no relations with the audited organization (e.g. customer, subcontractor, competition)',
                  {
                    defaultValue:
                      'the company in which I am employed has no relations with the audited organization (e.g. customer, subcontractor, competition)',
                  }
                )}
              </li>
            </ul>
            <span>
              {t(
                'In the event of an assessment at the manufacturer of medical devices, I also declare that:',
                {
                  defaultValue:
                    'In the event of an assessment at the manufacturer of medical devices, I also declare that:',
                }
              )}
            </span>
            <ul>
              <li>
                {t('I am not an authorized representative of the manufacturer', {
                  defaultValue: 'I am not an authorized representative of the manufacturer',
                })}
              </li>
              <li>
                {t(
                  'I did not provide advice to an authorized representative of the audited organization or a supplier of the audited organization in relation to the development, construction, marketing or maintenance of the products that are the subject of the audit',
                  {
                    defaultValue:
                      'I did not provide advice to an authorized representative of the audited organization or a supplier of the audited organization in relation to the development, construction, marketing or maintenance of the products that are the subject of the audit',
                  }
                )}
              </li>
              <li>
                {t(
                  'neither I nor my relatives have a financial interest (share in the company, shares...) or personal interests (family connections,...) regarding the organization under review',
                  {
                    defaultValue:
                      'neither I nor my relatives have a financial interest (share in the company, shares...) or personal interests (family connections,...) regarding the organization under review',
                  }
                )}
              </li>
            </ul>
          </Typography>
        </DialogContent>
      )}
      <DialogActions>
        <Button
          onClick={() => browseViewStore.setModalState('deny-statement')}
          disabled={isLoading}
        >
          {t('Deny statement', { defaultValue: 'Deny statement' })}
        </Button>
        <Button
          disabled={isLoading}
          onClick={() =>
            mutate({
              id: browseViewStore.auditInvitation?.id as string,
              data: { choice: 'confirm', lang: i18n.language },
            })
          }
        >
          {t('Accept statement', { defaultValue: 'Accept statement' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmAuditInvitationModal;
